/**
 * クライアントと Worker で共有する型定義。
 * このファイルは副作用ゼロ、型とリテラルのみを含めること。
 */

export const AREA_CODES = [
  "tokyo",
  "kanagawa",
  "hokkaido",
  "aichi",
  "osaka",
  "east",
  "west",
] as const;

export type AreaCode = (typeof AREA_CODES)[number];

/**
 * エリアの表示名と、Google Places Text Search に付与する補助語。
 * 補助語は「店名 + 補助語」で検索することで同名店舗の誤マッチを減らす。
 */
export const AREA_META: Record<AreaCode, { label: string; placesHint: string }> = {
  tokyo: { label: "東京", placesHint: "東京" },
  kanagawa: { label: "神奈川", placesHint: "神奈川県" },
  hokkaido: { label: "北海道", placesHint: "北海道" },
  aichi: { label: "愛知", placesHint: "愛知県" },
  osaka: { label: "大阪", placesHint: "大阪府" },
  east: { label: "東日本", placesHint: "日本" },
  west: { label: "西日本", placesHint: "日本" },
};

/** 食べログ百名店のカテゴリ (ラーメンのみ対応)。 */
export type HyakumeitenCategory = `ramen_${AreaCode}`;

export interface Selection {
  year: number;
  category: HyakumeitenCategory;
}

/**
 * 店舗マスターレコード。
 * 通常は `scripts/build-stores.mjs` が `tabelog.json` から自動生成する
 * (`src/shared/data/ramen-stores.generated.ts`)。
 *
 * placeId / lat / lng は基本的に空。Worker が Places API で遅延解決する。
 */
export interface StoreSeed {
  id: string;
  name: string;
  area: AreaCode;
  selections: Selection[];
  /** 都道府県 (例: 東京都)。tabelog の `pref` 由来。 */
  pref?: string;
  /** 近隣エリア (例: 新宿・代々木・大久保)。tabelog の `area` 由来。 */
  neighborhood?: string;
  /** 食べログ個別店舗 URL。ユーザーが手動管理する tabelog.json 経由でのみ取得。 */
  tabelogUrl?: string;
  /**
   * 店舗の営業ステータス。
   * - `"closed"`  閉店済み
   * - `"moved"`   別店舗 ID として扱われている移転先があり、この ID 自体は旧店舗
   * - `"renewed"` 同じ住所で店名変更/リニューアル。内容は別店舗 ID として扱われる
   * 未設定は「稼働中または不明」。
   */
  status?: "closed" | "moved" | "renewed";
  /** status を設定した理由 (UI には出さないが監査用) */
  statusReason?: string;
  /**
   * status === "moved" の場合、移転先として扱う別 store の id。
   * 移転先が百名店データ内にあるケース (例: 東京→神奈川の移転で ID が変わった) 用。
   * 移転先が百名店未選出で内部 store として存在しない場合は undefined のまま、
   * 代わりに movedToName / movedToUrl を直接埋める。
   */
  movedToId?: string;
  /** 移転先の店名。movedToId 経由なら build 時に cross-ref 注入、外部移転なら override で直接指定。 */
  movedToName?: string;
  /** 移転先の AreaCode (内部 store がある場合のみ)。クライアントのフィルタ切替に使う。 */
  movedToArea?: AreaCode;
  /**
   * 移転先 store が閉店している場合に true。build 時に target.status を見て自動注入。
   * 連鎖の最終地点が閉店のとき、source 側の見た目を「閉店扱い」に切り替えるためのフラグ。
   * MovedToCard 自体は引き続き表示され、ユーザーは移転先 store ページに遷移できる。
   */
  movedToClosed?: boolean;
  /**
   * 移転先エリアの表示ラベル (例: "静岡県")。AreaCode 列挙にない地域や、
   * 表示文字を明示したいときに override で指定する。
   * 未指定なら AREA_META[movedToArea].label にフォールバック。
   */
  movedToAreaLabel?: string;
  /**
   * 後継店舗 (移転先 / 関連店舗) の外部 URL。tabelog でも公式サイトでも可。
   * 内部 store として登録できないケース (海外進出、未選出店舗など) で使う。
   * status === "moved" の他、status === "closed" でも後継店舗の案内に使用できる。
   */
  movedToUrl?: string;
  /**
   * Places Text Search で正しく resolve できない店舗の救済用。
   * 値があれば resolve 時に「店名 + エリアヒント」ではなくこの住所を
   * そのままクエリに使い、address フィールドもこの値で上書きする。
   */
  manualAddress?: string;
  /**
   * true なら Place Details をフェッチせず、UI の「Google の情報」
   * セクションを非表示にする。Google 側に情報が存在しない閉店済み
   * 店舗などに使う。
   */
  hideGoogleInfo?: boolean;
  placeId?: string;
  lat?: number;
  lng?: number;
}

/** `/api/stores` が返す、集計プロパティを含む店舗表現。 */
export interface Store extends StoreSeed {
  selectionCount: number;
  firstSelectedYear: number;
  lastSelectedYear: number;
  years: number[];
  tabelogAwardUrls: Array<{ year: number; category: HyakumeitenCategory; url: string }>;
}

/** `/api/places/resolve` の応答。 */
export interface ResolvedPlace {
  placeId: string;
  lat: number;
  lng: number;
  address?: string;
}

/**
 * クライアントに返す写真エントリ。
 *
 * 月次同期スクリプト (`scripts/sync-places.mjs`) が Places Photo API から取得し
 * R2 バケット `PHOTOS` にアップロード済み。`key` は R2 オブジェクトキー
 * (例: `photos/13123456/0.jpg`)。ランタイムで Google には一切問い合わせない。
 */
export interface SnapshotPhoto {
  key: string;
  width: number;
  height: number;
}

/** `/api/places/:placeId` の応答。月次 snapshot から返る静的データ。 */
export interface PlaceDetails {
  placeId: string;
  name: string;
  address?: string;
  location: { lat: number; lng: number };
  rating?: number;
  userRatingCount?: number;
  priceLevel?: string;
  /**
   * 曜日別営業時間。`openNow` は時刻依存のためスナップショット化できず除外。
   * UI は `openNow` が無ければ営業中/営業時間外バッジを描画しない。
   */
  openingHours?: {
    weekdayDescriptions: string[];
  };
  photos: SnapshotPhoto[];
  googleMapsUri?: string;
  websiteUri?: string;
  internationalPhoneNumber?: string;
  /** この snapshot が Google Places から取得された時刻 (ISO8601)。UI に「取得日」として表示可能 */
  fetchedAt?: string;
}

/**
 * 月次同期スクリプトが書き出す静的スナップショット 1 件。
 * ランタイムでは `/api/places/resolve` と `/api/places/:placeId` 両エンドポイントの
 * データソースになる。
 */
export interface PlaceSnapshot extends PlaceDetails {
  /** `StoreSeed.id` と 1:1 対応。 */
  storeId: string;
}

/** `places-snapshot.generated.json` のトップレベル形状。 */
export interface PlaceSnapshotFile {
  /** スナップショット生成時刻 (ISO8601) */
  generatedAt: string;
  /** 同期対象の総店舗数 */
  totalStores: number;
  /** Places 解決に成功した店舗数 */
  resolvedStores: number;
  /** storeId → PlaceSnapshot マップ */
  byStoreId: Record<string, PlaceSnapshot>;
}

export interface ApiError {
  error: string;
  message?: string;
}
